type Spec = {
    fixed: {
        range: [number, number];
        count: number;
    }[];
    percent: {
        range: [number, number];
        percent: number;
    }[];
}

function parseSpec(totalDraws: number, spec: string): Spec {
    const lines = spec.trim().split(/\r?\n/);
    const fixedDraws: Spec['fixed'] = [];
    const percentageDraws: Spec['percent'] = [];

    for (const line of lines) {
        const parts = line.trim().split(/\s+/);
        if (parts.length === 0 || !parts[0]) continue;

        const rangePart = parts[0];
        let low,high;
        if (rangePart.includes('-')) {      // 13.5-13.9 ...
            [low, high] = rangePart.split('-').map(parseFloat);
        } else {
            if (rangePart.includes(".")) {  // 13.7 ...
                let level = parseFloat(rangePart);
                [low, high] = [level, level];
            } else {
                if (rangePart.endsWith("+")) {      // 13+ ..., 14+ ...
                    let level = parseFloat(rangePart.substring(0, rangePart.length-1));
                    [low, high] = [level+0.7, level+0.9];
                } else {                            // 13 ..., 14 ...
                    let level = parseFloat(rangePart);
                    [low, high] = [level, level+0.6];
                }
            }
        }

        const last = parts[parts.length - 1];
        if (last.includes('%')) {
            const percent = parseFloat(last.replace('%', ''));
            percentageDraws.push({ range: [low, high], percent });
        } else {
            const count = parseInt(parts[1], 10);
            fixedDraws.push({ range: [low, high], count });
        }
    }

    const totalFixed = fixedDraws.reduce((sum, item) => sum + item.count, 0);
    let remaining = totalDraws - totalFixed;
    if (remaining < 0) {
        throw new Error("Total fixed draws exceed total draws.");
    }

    const totalPercent = percentageDraws.reduce((sum, item) => sum + item.percent, 0);
    if (totalPercent > 100) {
        throw new Error("Total percentage exceeds 100%");
    }

    return {fixed: fixedDraws, percent: percentageDraws}
}

function calculateNumbers(remaining: number, percentageDraws: Spec['percent']): {range: [number, number], count: number}[] {

    // Calculate counts based on percentages
    const percentageCounts = percentageDraws.map(item => {
        const count = Math.round((item.percent / 100) * remaining);
        return { range: item.range, count };
    });

    // Adjust rounding difference
    const distributed = percentageCounts.reduce((sum, item) => sum + item.count, 0);
    const diff = remaining - distributed;
    if (diff !== 0 && percentageCounts.length > 0) {
        // Add difference to the largest group
        let maxIndex = 0;
        for (let i = 1; i < percentageCounts.length; i++) {
            if (percentageCounts[i].count > percentageCounts[maxIndex].count) {
                maxIndex = i;
            }
        }
        percentageCounts[maxIndex].count += diff;
    }

    return percentageCounts;
}

type Card = {level: number};

export function myCustomDraw<T>(
    totalDraws: number, 
    spec: string, 
    deck: Card[] = [], 
    realDeck: T[] = [],
    draw: (low: number, high: number) => T|null,
): T[] {
    totalDraws += realDeck.length;
    let {fixed: fixedDraws, percent: percentageDraws} = parseSpec(totalDraws, spec);

    function inRange(range: [number, number], level: number) {
        return (range[0] <= level) && (level <= range[1]);
    }

    // remove existing fixed draws
    for (let f of fixedDraws) {
        let toDelete: number[] = [];
        for (let [i,card] of deck.entries()) {
            if (inRange(f.range, card.level)) {
                toDelete.push(i);
            }

            if (toDelete.length >= f.count) {
                break;
            }
        }

        f.count -= toDelete.length;
        for (let i of toDelete.reverse()) {
            deck.splice(i, 1);
        }
    }

    const totalFixed = fixedDraws.reduce((sum, item) => sum + item.count, 0);
    let remaining = totalDraws - totalFixed;

    let percentageCounts = calculateNumbers(remaining, percentageDraws);

    // remove percentage draws
    for (let f of percentageCounts) {
        let toDelete: number[] = [];
        for (let [i,card] of deck.entries()) {
            if (inRange(f.range, card.level)) {
                toDelete.push(i);
            }

            if (toDelete.length >= f.count) {
                break;
            }
        }

        f.count -= toDelete.length;
        for (let i of toDelete.reverse()) {
            deck.splice(i, 1);
        }
    }

    // Draw all cards
    const newDeck = realDeck;
    const allDraws = [...percentageCounts, ...fixedDraws];
    for (const { range, count } of allDraws) {
        let [low, high] = range;

        for (let i = 0; i < count; i++) {
            let selection = draw(low, high);
            if (selection !== null) {
                newDeck.push(selection);
            }
        }
    }
    return newDeck;
}
